/**
 * The users third of the Admin surface: list, create, detail (roles, overrides,
 * grp-only record actions). Phase 2.5b Part B.
 *
 * Named `routes.users.tsx` for the reason `routes.infra.tsx` states in its own
 * header: two work streams land screens in this folder and must not race for
 * one filename. `src/routes.extra.ts` is where the slices meet.
 *
 * ===========================================================================
 * WHY `/countries/:cc/admin/users` AND NOT A TOP-LEVEL `/admin/users`
 * ===========================================================================
 * Users are GLOBAL objects (owner decision D5 — the `users` table carries no
 * `country_id`, and the API routes these screens call carry no `:cc`). A
 * country-less `/admin/users` would say that more honestly in the address bar —
 * but it would have to mount OUTSIDE `RequireCountry`, which means outside
 * `AppShell`: a second shell instance, a TopBar whose `CountrySwitcher` has no
 * current country, and an exception to the design decision recorded at the top
 * of `src/routes.tsx` ("every in-app path is /countries/:cc/..."), for three
 * screens. The `:cc` here scopes the CHROME, not the data:
 *
 *   · `RequireCountry` still answers only "may this user stand in this
 *     country" — every `users.manage` holder can, in at least one;
 *   · the API calls behind these screens are unscoped — `CountryScopeGuard`
 *     never sees them, and switching country does not change the list;
 *   · what the `:cc` DOES feed is D5 itself: the create form's in-scope role
 *     options and the role editor's editable-country boundary are computed
 *     against the caller's per-country `users.manage`, and defaulting the
 *     first role row to the country on screen is the least surprising choice.
 *
 * The asymmetry — country in the web path, none in the API path — is deliberate
 * and recorded here rather than discovered in devtools.
 */

import type { RouteObject } from 'react-router-dom';
import { UsersListPage } from './UsersListPage';
import { UserCreatePage } from './UserCreatePage';
import { UserDetailPage } from './UserDetailPage';

export const adminUsersRoutes: RouteObject[] = [
  { path: 'admin/users', element: <UsersListPage /> },
  // Declared before `:userId` for the reader; the router matches the static
  // segment first regardless.
  { path: 'admin/users/new', element: <UserCreatePage /> },
  { path: 'admin/users/:userId', element: <UserDetailPage /> },
];
